import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi, type ApiRequestOptions } from "./useApi";
import { useShipToApi } from "./useShipToApi";
import { useSessionSetAtom } from "../useSessionSetAtom";
import { createReadGuard } from "./_helpers/readGuard";
import { assertArrayPayload } from "@utils/apiPayload";
import {
    productsAtom,
    isFetchingProductsAtom,
    isProcessingProductsAtom,
} from "@/store/productAtoms";
import type {
    CreateProductRequest,
    CreateProductResponse,
    FeaturedListProduct,
    GetAllProductsResponse,
    PopularProduct,
    Product,
    RelatedProduct,
    UpdateProductRequest,
} from "@models/product";
import type { GetProductsQuery } from "@models/productFilter";
import { fetchDownload, type DownloadedFile } from "@utils/fileDownload";

const PRODUCTS_ENDPOINT = "/products";

// #476: a COA is served with the type recorded at upload, one of the API's
// AllowedCoaTypes.
const COA_CONTENT_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
];

// Which list read is allowed to publish to productsAtom / isFetchingProductsAtom. Module-scope for
// the same reason useProductFacetApi's is: the atoms are module-scope (the app mounts no Jotai
// Provider) while any caller's `cancelled` flag is per-mount, so only a sequence answers "has a
// newer read taken over?" — and a suppressed write always has a successor that publishes in its
// place. Switching the ship-to location is exactly the event that supersedes a read mid-flight
// (#228 / E4-S12): without this, the previous destination's answer still lands in the atoms once
// it settles, long after the header says the shopper moved.
//
// The detail read (getProduct) publishes to no atom, so it needs no guard: its callers keep the
// read's outcome in their own state (#407). Latest-only (`isLatest`).
const productListReads = createReadGuard();

export const useProductApi = () => {
    const { callApi } = useApi();
    const { callShipToApi } = useShipToApi();
    // Session-bound setters for what endSessionAtom clears; the in-flight
    // flags keep plain setters so their `finally` always lands.
    const setProducts = useSessionSetAtom(productsAtom);
    const setIsFetching = useSetAtom(isFetchingProductsAtom);
    const setIsProcessing = useSetAtom(isProcessingProductsAtom);

    const createProduct = useCallback(
        async (data: CreateProductRequest): Promise<CreateProductResponse> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                formData.append("Body.CategoryId", data.categoryId.toString());
                formData.append("Body.Serialized", String(data.serialized));
                formData.append("Body.DiscountAllowed", String(data.discountAllowed));
                formData.append("Body.Msrp", data.msrp.toString());

                if (data.name) formData.append("Body.Name", data.name);
                if (data.description) formData.append("Body.Description", data.description);
                if (data.upc) formData.append("Body.Upc", data.upc);
                if (data.ean) formData.append("Body.Ean", data.ean);
                if (data.manufacturerSku) formData.append("Body.ManufacturerSku", data.manufacturerSku);
                if (data.brand) formData.append("Body.Brand", data.brand);

                // Lists are indexed (`Body.LegalClasses[0]`, …): the API's
                // multipart binder reads `Name[i]` and stops at the first
                // missing index, so repeated bare keys would bind nothing.
                if (data.legalClasses && data.legalClasses.length > 0) {
                    data.legalClasses.forEach((legalClass, i) => {
                        formData.append(`Body.LegalClasses[${i}]`, legalClass.toString());
                    });
                }

                if (data.tags && data.tags.length > 0) {
                    data.tags.forEach((tag, i) => {
                        formData.append(`Body.Tags[${i}]`, tag);
                    });
                }

                if (data.variants && data.variants.length > 0) {
                    data.variants.forEach((v, i) => {
                        if (v.name) formData.append(`Body.Variants[${i}].Name`, v.name);
                        if (v.description) formData.append(`Body.Variants[${i}].Description`, v.description);
                        if (v.options && v.options.length > 0) {
                            v.options.forEach((id, j) => {
                                formData.append(`Body.Variants[${i}].Options[${j}]`, id.toString());
                            });
                        }
                        formData.append(`Body.Variants[${i}].Msrp`, v.msrp.toString());
                    });
                }

                // Files are NOT indexed: the binder collects every file part
                // named `Body.Images`, in the order sent, as addImages sends them.
                data.images?.forEach((file) => {
                    formData.append("Body.Images", file);
                });

                // #476: optional; under PUT products/{id}/coa's rules.
                if (data.coa) formData.append("Body.Coa", data.coa);

                return await callApi<CreateProductResponse>(PRODUCTS_ENDPOINT, {
                    method: "POST",
                    body: formData,
                    contentType: "multipart",
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const getProducts = useCallback(
        async (query: GetProductsQuery): Promise<GetAllProductsResponse> => {
            const { isLatest } = productListReads.begin();

            setIsFetching(true);

            try {
                const queryString = buildQueryString(query);
                const url = queryString
                    ? `${PRODUCTS_ENDPOINT}?${queryString}`
                    : PRODUCTS_ENDPOINT;
                const response =
                    await callShipToApi<GetAllProductsResponse>(url);

                if (isLatest()) setProducts(response);

                return response;
            } finally {
                if (isLatest()) setIsFetching(false);
            }
        },
        [callShipToApi, setProducts, setIsFetching],
    );

    // `skipErrorNotification` is opt-in for a caller that renders its own
    // inline failed state (BrowsePopular) — one failure must not read as a
    // toast AND a panel. Left out, callApi's own default applies and the
    // toast stays. Shape-checked for the reason getFeatured gives below.
    const getPopular = useCallback(
        async (
            limit?: number,
            options: { skipErrorNotification?: boolean } = {},
        ): Promise<PopularProduct[]> => {
            const endpoint = `${PRODUCTS_ENDPOINT}/popular`;
            const url = limit != null ? `${endpoint}?limit=${limit}` : endpoint;
            const rows = await callShipToApi<PopularProduct[]>(url, {
                skipErrorNotification: options.skipErrorNotification,
            });
            assertArrayPayload(endpoint, rows);
            return rows;
        },
        [callShipToApi],
    );

    // GET /products/featured (#401). Plain read, no atoms — like getPopular —
    // so the browse pages that use it never touch the admin catalogue's
    // productsAtom. The toast is suppressed for the same reason
    // getRelatedProductsBatch and getFeaturedProduct suppress theirs (#307): the
    // rail already has its own inline failed state.
    // Shape-checked like the other list reads: a 2xx without a JSON content
    // type resolves a string, and the rail's `.map()` over it would be a
    // render-path TypeError — GlobalError, not the rail's own failed state.
    const getFeatured = useCallback(
        async (): Promise<FeaturedListProduct[]> => {
            const endpoint = `${PRODUCTS_ENDPOINT}/featured`;
            const rows = await callShipToApi<FeaturedListProduct[]>(endpoint, {
                skipErrorNotification: true,
            });
            assertArrayPayload(endpoint, rows);
            return rows;
        },
        [callShipToApi],
    );

    // GET /products/related (#449, audit B-05): the cart's Related Items rail
    // in ONE read, the products related to any of `productIds`, which the API
    // scores, sums, orders and caps at six, the given products excluded. The
    // ids go as repeated keys; the API takes 1 to 50, so the caller caps them.
    // The toast is suppressed (#307): the rail renders its own failed state
    // with Retry. Shape-checked for the reason getFeatured gives above.
    const getRelatedProductsBatch = useCallback(
        async (productIds: string[]): Promise<RelatedProduct[]> => {
            const endpoint = `${PRODUCTS_ENDPOINT}/related`;
            const query = new URLSearchParams();
            productIds.forEach((id) => query.append("productIds", id));
            const rows = await callShipToApi<RelatedProduct[]>(
                `${endpoint}?${query.toString()}`,
                { skipErrorNotification: true },
            );
            assertArrayPayload(endpoint, rows);
            return rows;
        },
        [callShipToApi],
    );

    // `includeInactive` is an admin-only read flag (#294): the API honours it
    // for the Admin role and silently ignores it otherwise. It is opt-in per
    // call — the edit page asks for it, the customer product page never sends
    // it — so the param is appended only when asked, never as `=false`.
    // `skipErrorNotification` is forwarded as given, for callers that render
    // their own failed / not-found cell so the designed page is not
    // accompanied by the generic `Error 404` toast: `true` from the edit
    // page's mount read (both cells), a 404-only predicate from the storefront
    // product page (its failure cell keeps the toast).
    const getProduct = useCallback(
        async (
            id: string,
            options: {
                includeInactive?: boolean;
                skipErrorNotification?: ApiRequestOptions["skipErrorNotification"];
            } = {},
        ): Promise<Product> => {
            const url = options.includeInactive
                ? `${PRODUCTS_ENDPOINT}/${id}?includeInactive=true`
                : `${PRODUCTS_ENDPOINT}/${id}`;
            return callShipToApi<Product>(
                url,
                options.skipErrorNotification
                    ? { skipErrorNotification: options.skipErrorNotification }
                    : {},
            );
        },
        [callShipToApi],
    );

    // `skipErrorNotification` is forwarded as given: the edit page silences
    // the toast for a 409 only (AC-09), which it answers with its own alert.
    const updateProduct = useCallback(
        async (
            id: string,
            data: UpdateProductRequest,
            options: {
                skipErrorNotification?: ApiRequestOptions["skipErrorNotification"];
            } = {},
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${PRODUCTS_ENDPOINT}/${id}`, {
                    method: "PUT",
                    body: JSON.stringify(data),
                    skipErrorNotification: options.skipErrorNotification,
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const deleteProduct = useCallback(
        async (id: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${PRODUCTS_ENDPOINT}/${id}`, {
                    method: "DELETE",
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const addImages = useCallback(
        async (productId: string, files: File[]): Promise<void> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                files.forEach((file) => {
                    formData.append("Body.Images", file);
                });

                await callApi(`${PRODUCTS_ENDPOINT}/${productId}/images`, {
                    method: "POST",
                    body: formData,
                    contentType: "multipart",
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    const removeImage = useCallback(
        async (productId: string, imageId: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(
                    `${PRODUCTS_ENDPOINT}/${productId}/images/${imageId}`,
                    {
                        method: "DELETE",
                        contentType: "none",
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // Sets SortOrder by position and IsPrimary exclusively. The endpoint
    // requires the FULL ordered set of the product's image ids (#170) — a
    // partial list is rejected with 400.
    const orderImages = useCallback(
        async (
            productId: string,
            imageIds: string[],
            primaryImageId: string,
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(
                    `${PRODUCTS_ENDPOINT}/${productId}/images/order`,
                    {
                        method: "PUT",
                        body: JSON.stringify({ imageIds, primaryImageId }),
                    },
                );
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // #476 (DecisionLog I3): attaches the product's COA, or replaces the one it
    // has. Like the image writes, it raises isProcessing, which holds the edit
    // page's Save and Reload while it is out. It does not move the product's
    // concurrency stamp. `skipErrorNotification` is forwarded as given: the
    // edit page answers a 409 itself.
    const uploadCoa = useCallback(
        async (
            productId: string,
            file: File,
            options: {
                skipErrorNotification?: ApiRequestOptions["skipErrorNotification"];
            } = {},
        ): Promise<void> => {
            setIsProcessing(true);

            try {
                const formData = new FormData();

                formData.append("Body.File", file);

                await callApi(`${PRODUCTS_ENDPOINT}/${productId}/coa`, {
                    method: "PUT",
                    body: formData,
                    contentType: "multipart",
                    skipErrorNotification: options.skipErrorNotification,
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // #476: removes the product's COA (a 204 when it has none).
    const removeCoa = useCallback(
        async (productId: string): Promise<void> => {
            setIsProcessing(true);

            try {
                await callApi(`${PRODUCTS_ENDPOINT}/${productId}/coa`, {
                    method: "DELETE",
                    contentType: "none",
                });
            } finally {
                setIsProcessing(false);
            }
        },
        [callApi, setIsProcessing],
    );

    // #476: the COA's file. The container is private, so the bytes come back
    // through callApi (bearer token) and are saved client-side. "contracted":
    // the API's own 404 (no COA, or its file is gone) rejects as the ApiError
    // for the caller's sentence, with no toast; every other failure keeps
    // useApi's toast and rejects as DownloadError. A 2xx that is not a COA
    // (an empty body, a gateway's page) rejects as DownloadError too.
    const downloadCoa = useCallback(
        (productId: string): Promise<DownloadedFile> =>
            fetchDownload(callApi, `${PRODUCTS_ENDPOINT}/${productId}/coa`, {
                failures: "contracted",
                acceptedTypes: COA_CONTENT_TYPES,
            }),
        [callApi],
    );

    return {
        createProduct,
        getProducts,
        getPopular,
        getFeatured,
        getRelatedProductsBatch,
        getProduct,
        updateProduct,
        deleteProduct,
        addImages,
        removeImage,
        orderImages,
        uploadCoa,
        removeCoa,
        downloadCoa,
    };
};

// TODO: move to helpers
const buildQueryString = (params: GetProductsQuery): string => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, String(item)));
        } else {
            searchParams.append(key, String(value));
        }
    });

    return searchParams.toString();
};
